import { useEffect, useState } from 'react';
import { getMsLeftForTimer as getMsLeft } from '../selectors';

// Tick faster than 1s so the display does not skip a second. State changes once per second.
const TICK_MS = 500;

type Args = {
    // Unix seconds. Set once the student launches their first lesson.
    timeRunsOutAt: number | null;

    // Must already include the extendExamTime accommodation. Callers get it from getTimeLimitHours.
    timeLimitHours: number | null;
};

/**
 * Mirrors Stream#msLeftInTimeLimit, and keeps counting down while the timer is running. Before the
 * timer starts there is nothing to count, so no interval is set.
 */
export const useMsLeftInTimeLimit = ({ timeRunsOutAt, timeLimitHours }: Args) => {
    const [msLeft, setMsLeft] = useState(() => getMsLeft({ timeRunsOutAt, timeLimitHours }));

    useEffect(() => {
        setMsLeft(getMsLeft({ timeRunsOutAt, timeLimitHours }));

        if (!timeRunsOutAt) return undefined;

        const interval = setInterval(() => {
            setMsLeft(previous => {
                const next = getMsLeft({ timeRunsOutAt, timeLimitHours });

                // Only re-render when the displayed second changes.
                if (previous !== null && next !== null && Math.floor(previous / 1000) === Math.floor(next / 1000)) {
                    return previous;
                }
                return next;
            });
        }, TICK_MS);

        return () => clearInterval(interval);
    }, [timeRunsOutAt, timeLimitHours]);

    return msLeft;
};
